import React from 'react';
import { Typography, Card, Space, Tag } from 'antd';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import {toHrsMinsSecs} from '../../../libs/formatLib';
import {OTR_THRESHOLDS} from '../utils/OTRAnalytics';

const { Text } = Typography;

// Component to display trip variance data in a chart
const OTRTripChart = ({ data }) => {
  // Custom tooltip to show detailed stop information
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const stopData = payload[0].payload;
      const variance = stopData.variance;
      
      // Determine status color based on variance
      let color = 'green';
      let statusText = 'On Time';
      let icon = <CheckCircleOutlined />;
      
      if (variance < -2) {
        color = 'red';
        statusText = 'Early';
        icon = <WarningOutlined />;
      } else if (variance > 5) {
        color = 'blue';
        statusText = 'Late';
        icon = <CloseCircleOutlined />;
      }
      
      return (
        <Card size="small" style={{ border: `1px solid ${color}` }}>
          <Space direction="vertical" size="small">
            <Text strong>Stop {stopData.sequence}</Text>
            <Text>TSN: {stopData.stopId || 'Unknown'}</Text>
            <Text>
              Variance: 
              <Text style={{ color }}>
                {variance > 0 ? ' +' : ' '}{toHrsMinsSecs(variance)}
              </Text>
            </Text>
            <Tag color={color} icon={icon}>{statusText}</Tag>
          </Space>
        </Card>
      );
    }
    
    return null;
  };
  
  // Find min and max variance in your data
  const variances = data.map(d => d.variance);
  const minVar = Math.min(...variances);
  const maxVar = Math.max(...variances);

  // Round min down and max up to the nearest minute (assuming variance is in seconds)
  const minTick = Math.floor(minVar / 60) * 60;
  const maxTick = Math.max(Math.ceil(maxVar / 60) * 60, 120); // Ensure at least 2m (120s)

  // Generate ticks at every minute
  const ticks = [];
  for (let t = minTick; t <= maxTick; t += 60) {
    ticks.push(t);
  }
  
  return (
    <div style={{ width: '100%', height: 400 }}>
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="sequence" 
            label={{ value: 'Stop Sequence', position: 'insideBottomRight', offset: -10 }}
          />
          <YAxis 
            label={{ 
              value: 'Variance', 
              angle: -90, 
              position: 'insideLeft',
              offset: 0
            }}
            tickFormatter={(value) => toHrsMinsSecs(value)}
            ticks={ticks}
            domain={[minTick, maxTick]}
          />
          <RechartsTooltip content={<CustomTooltip />} />
          
          {/* On-time reference lines */}
          <ReferenceLine 
            y={OTR_THRESHOLDS.onTime}
            stroke="#666" 
            strokeDasharray="3 3" 
            label={{ value: 'On Time', position: 'insideTopRight', fill: '#666' }}
          />
          <ReferenceLine 
            y={OTR_THRESHOLDS.late}
            stroke="#1890ff" 
            strokeDasharray="3 3" 
            label={{ value: 'Late Threshold', position: 'insideBottomRight', fill: '#1890ff' }}
          />
          <ReferenceLine 
            y={OTR_THRESHOLDS.early}
            stroke="#f5222d" 
            strokeDasharray="3 3" 
            label={{ value: 'Early Threshold', position: 'insideTopRight', fill: '#f5222d' }}
          />
          
          {/* The variance line */}
          <Line
            name="Time Variance"
            type="monotone"
            dataKey="variance"
            stroke="#1890ff"
            strokeWidth={2}
            activeDot={{ r: 8 }}
            dot={{ 
              r: 6, 
              strokeWidth: 2,
              fill: 'white',
              stroke: (entry) => {
                const variance = entry.variance;
                if (variance === undefined || variance === null) return '#666';
                if (variance < -2) return '#f5222d'; // early - red
                if (variance > 5) return '#1890ff';  // late - blue
                return '#52c41a'; // on time - green
              }
            }}
          />
          <Legend verticalAlign="bottom" align="center" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default OTRTripChart; 